import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Table from "./DoctorTable";
import PageHeading from "../../PageHeading";
import DynamicSelect from "../../DynamicSelect";
import { useTranslation } from "react-i18next";
import ConfirmActionModal from "../../ConfirmDeleteModal";

const DoctorList = () => {
  const { t, i18n } = useTranslation();
  const [doctors, setDoctors] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [filteredDoctors, setFilteredDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedLanguage, setSelectedLanguage] = useState("en");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [doctorsToDelete, setDoctorsToDelete] = useState(null);

  const navigate = useNavigate();
  const API_KEY = "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";

  // Fetch All Doctors and Departments
  useEffect(() => {
    const fetchDoctors = async () => {
      try {
        setLoading(true);
        const response = await axios.get("https://api.muktihospital.com/api/doctor", {
          headers: { "x-api-key": API_KEY },
        });
        const doctorsData = Array.isArray(response.data) ? response.data : [];
        setDoctors(doctorsData);
        setFilteredDoctors(doctorsData);
      //  console.log(doctors);

        // Extract unique departments
        const uniqueDepartments = [
          ...new Set(
            doctorsData.map((doctor) => doctor.translations[selectedLanguage]?.department)
          ),
        ].filter(Boolean);
        setDepartments(uniqueDepartments);
      } catch (error) {
        console.error("Error fetching doctors:", error);
        toast.error("Failed to fetch doctor list");
      } finally {
        setLoading(false);
      }
    };
    fetchDoctors();
  }, [selectedLanguage]);

  // Handle Search
  const handleSearch = (event) => {
    const term = event.target.value.toLowerCase();
    setSearchTerm(term);
    filterDoctors(term, selectedLanguage);
  };

  // Handle Language Change
  const languageOptions = [
    { value: "en", label: "English" },
    { value: "bn", label: "Bangla" },
  ];

  const handleLanguageChange = (selectedOption) => {
    const lang = selectedOption.value;
    setSelectedLanguage(lang);
    i18n.changeLanguage(lang);
    filterDoctors(searchTerm, lang);
  };

  // Filter doctors based on the search term and language
  const filterDoctors = (term, language, doctorList = doctors) => {
    const filtered = doctorList.filter((doctor) => {
      const name = doctor.translations?.[language]?.name?.toLowerCase() || "";
      const department = doctor.translations?.[language]?.department?.toLowerCase() || "";
      const email = doctor.email?.toLowerCase() || "";
      return name.includes(term) || department.includes(term) || email.includes(term);
    });
    setFilteredDoctors(filtered);
  };

  // Open Delete Modal
  const openDeleteModal = (doctorIds) => {
    setDoctorsToDelete(doctorIds);
    setDeleteModalOpen(true);
  };

  // Close Delete Modal
  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setDoctorsToDelete(null);
  };

  // Confirm Delete
  const handleConfirmDelete = async (doctorIds) => {
    const ids = Array.isArray(doctorIds) ? doctorIds : [doctorIds];
    try {
      await Promise.all(
        ids.map((id) =>
          axios.delete(`https://api.muktihospital.com/api/doctor/delete/${id}`, {
            headers: { "x-api-key": API_KEY },
          })
        )
      );
      const updatedDoctors = doctors.filter((doctor) => !ids.includes(doctor.id));
      setDoctors(updatedDoctors);
      filterDoctors(searchTerm, selectedLanguage, updatedDoctors);
      toast.success(`Doctor${ids.length > 1 ? "s" : ""} deleted successfully!`);
    } catch (error) {
      console.error("Error deleting doctor(s):", error);
      toast.error("Failed to delete doctor(s).");
    }
  };

  // Handle Edit
  const handleEdit = (id) => {
    navigate(`/edit-doctor/${id}`);
  };

  // Handle Add
  const handleAdd = () => {
    navigate("/add-doctor");
  };

  // Breadcrumbs
  const breadcrumbs = [
    { label: "Dashboard", url: "/" },
    { label: "Manage Doctors"},
    { label: "All Doctors" },
  ];

  const DoctorColumns = [
    "SL No",
    "name",
    "specialty",
    "phone number",
    "email",
    "schedule",
    "Experience",
  ];

  return (
    <div>
      <ToastContainer position="top-right" />
      <ConfirmActionModal
        isOpen={deleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={handleConfirmDelete}
        ids={doctorsToDelete}
        entityName="doctor"
        title="Confirm Delete"
        message={
          Array.isArray(doctorsToDelete) && doctorsToDelete.length > 1
            ? `Are you sure you want to delete these ${doctorsToDelete.length} doctors?`
            : "Are you sure you want to delete this doctor?"
        }
        confirmText="Delete"
        cancelText="Cancel"
      />
      <PageHeading title="All Doctors" breadcrumbs={breadcrumbs} />
      <div className="flex justify-between items-center mb-4">
        <DynamicSelect
          options={languageOptions}
          onChange={handleLanguageChange}
          value={languageOptions.find((option) => option.value === selectedLanguage)}
          isSearchable
        />
      </div>
      <div>
        <Table
          data={filteredDoctors}
          setData={setFilteredDoctors}
          columns={DoctorColumns}
          selectedLanguage={selectedLanguage}
          searchTerm={searchTerm}
          handleSearch={handleSearch}
          handleEdit={handleEdit}
          handleAdd={handleAdd}
          openDeleteModal={openDeleteModal}
          departments={departments}
          loading={loading}
        />
      </div>
    </div>
  );
};

export default DoctorList;
